import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Building2, Users, UserPlus, CheckCircle, ArrowRight, Shield, KeyRound } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Onboarding = () => {
  const { token, user, refreshUser, refreshCondominiums } = useAuth();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Password change form
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Privacy consent
  const [privacyConsent, setPrivacyConsent] = useState(false);
  
  // Condominium form
  const [condoForm, setCondoForm] = useState({
    name: '',
    address: '',
    city: '',
    postal_code: '',
    vat_number: ''
  });
  
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        
        // If onboarding complete, redirect
        if (!data.needs_onboarding) {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
    setLoading(false);
  }, [token, navigate]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('La password deve essere almeno 8 caratteri');
      return;
    }
    
    setSubmitting(true);
    try {
      // La rotta vuole un corpo JSON (current_password/new_password): con la query
      // string rispondeva 422 e il wizard non riusciva mai a cambiare la password.
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: oldPassword, new_password: newPassword })
      });
      
      if (res.ok) {
        toast.success('Password modificata');
        if (refreshUser) await refreshUser();
        fetchStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
    setSubmitting(false);
  };

  // Accept privacy
  const handleAcceptPrivacy = async () => {
    if (!privacyConsent) {
      toast.error('Devi accettare l\'informativa privacy');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/accept-privacy`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Consenso registrato');
        if (refreshUser) await refreshUser();
        fetchStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
    setSubmitting(false);
  };

  // Create condominium
  const handleCreateCondominium = async (e) => {
    e.preventDefault();
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/condominiums`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(condoForm)
      });
      
      if (res.ok) {
        toast.success('Condominio creato');
        // Aggiorna il contesto: carica i condomìni e auto-seleziona il primo
        // (altrimenti l'utente resterebbe con "Nessun ruolo" e menu nascosto).
        if (refreshCondominiums) await refreshCondominiums();
        fetchStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore');
      }
    } catch (error) {
      toast.error('Errore');
    }
    setSubmitting(false);
  };

  // Navigate to registry
  const goToRegistry = () => {
    navigate(`/registry`);
  };

  // Navigate to invites
  const goToInvites = () => {
    navigate(`/invites`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 'password_change', label: 'Cambia Password', icon: KeyRound },
    { id: 'privacy_consent', label: 'Privacy', icon: Shield },
    { id: 'create_condominium', label: 'Crea Condominio', icon: Building2 },
    { id: 'add_units', label: 'Aggiungi Unità', icon: Building2 },
    { id: 'add_people', label: 'Aggiungi Persone', icon: Users },
    { id: 'invite_residents', label: 'Invita Residenti', icon: UserPlus },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === status?.step);
  const progress = status?.needs_onboarding 
    ? ((currentStepIndex + 1) / steps.length) * 100 
    : 100;

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Benvenuto su Artego</h1>
          <p className="text-muted-foreground">
            Completa la configurazione iniziale per iniziare
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Configurazione</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Steps indicator */}
        <div className="flex justify-center gap-2 mb-8">
          {steps.slice(0, 4).map((step, i) => {
            const isActive = step.id === status?.step;
            const isComplete = currentStepIndex > i;
            const StepIcon = step.icon;
            
            return (
              <div 
                key={step.id}
                className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  isComplete ? 'bg-green-500 text-white' :
                  isActive ? 'bg-slate-900 text-white' :
                  'bg-slate-200 text-slate-500'
                }`}
              >
                {isComplete ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <StepIcon className="h-5 w-5" />
                )}
              </div>
            );
          })}
        </div>

        {/* Current Step Content */}
        <Card>
          {status?.step === 'password_change' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <KeyRound className="h-5 w-5" />
                  Cambia Password
                </CardTitle>
                <CardDescription>
                  Per motivi di sicurezza, devi cambiare la password temporanea
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Password attuale</Label>
                    <Input
                      type="password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="Password temporanea ricevuta"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nuova password</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Almeno 8 caratteri"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Conferma password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Ripeti la nuova password"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Salvataggio...' : 'Cambia Password'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {status?.step === 'privacy_consent' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Informativa Privacy
                </CardTitle>
                <CardDescription>
                  Leggi e accetta l'informativa sulla privacy per continuare
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg max-h-64 overflow-y-auto text-sm">
                  <h4 className="font-semibold mb-2">Informativa ai sensi del GDPR</h4>
                  <p className="mb-2">
                    Artego Condo Manager tratta i tuoi dati personali per la gestione
                    del condominio secondo quanto previsto dal Regolamento UE 2016/679 (GDPR).
                  </p>
                  <p className="mb-2">
                    I dati raccolti includono: nome, cognome, codice fiscale, email, 
                    telefono e dati relativi alla proprietà immobiliare.
                  </p>
                  <p className="mb-2">
                    I dati sono trattati per le seguenti finalità:
                  </p>
                  <ul className="list-disc pl-5 mb-2">
                    <li>Gestione amministrativa del condominio</li>
                    <li>Comunicazioni relative alle assemblee</li>
                    <li>Gestione contabile e fiscale</li>
                    <li>Adempimenti di legge</li>
                  </ul>
                  <p>
                    Hai diritto di accedere, rettificare, cancellare i tuoi dati e 
                    opporti al trattamento contattando l'amministratore.
                  </p>
                </div>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="privacy"
                    checked={privacyConsent}
                    onCheckedChange={(c) => setPrivacyConsent(!!c)}
                  />
                  <Label htmlFor="privacy" className="cursor-pointer leading-relaxed">
                    Ho letto e accetto l'informativa sulla privacy e acconsento al 
                    trattamento dei miei dati personali
                  </Label>
                </div>
                
                <Button 
                  className="w-full" 
                  disabled={!privacyConsent || submitting}
                  onClick={handleAcceptPrivacy}
                >
                  {submitting ? 'Salvataggio...' : 'Accetta e Continua'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {status?.step === 'create_condominium' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Crea il tuo primo Condominio
                </CardTitle>
                <CardDescription>
                  Inserisci i dati del condominio che gestirai
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCondominium} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Nome Condominio *</Label>
                    <Input
                      value={condoForm.name}
                      onChange={(e) => setCondoForm({ ...condoForm, name: e.target.value })}
                      placeholder="Es: Condominio Via Roma 1"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Indirizzo *</Label>
                    <Input
                      value={condoForm.address}
                      onChange={(e) => setCondoForm({ ...condoForm, address: e.target.value })}
                      placeholder="Via, numero civico"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Città *</Label>
                      <Input
                        value={condoForm.city}
                        onChange={(e) => setCondoForm({ ...condoForm, city: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CAP *</Label>
                      <Input
                        value={condoForm.postal_code}
                        onChange={(e) => setCondoForm({ ...condoForm, postal_code: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Partita IVA (opzionale)</Label>
                    <Input
                      value={condoForm.vat_number}
                      onChange={(e) => setCondoForm({ ...condoForm, vat_number: e.target.value })}
                      placeholder="IT12345678901"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Creazione...' : 'Crea Condominio'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {status?.step === 'add_units' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Aggiungi Unità Immobiliari
                </CardTitle>
                <CardDescription>
                  Configura le unità del condominio (appartamenti, box, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Vai alla sezione Anagrafica per aggiungere le unità immobiliari
                  del condominio (appartamenti, box, cantine, etc.)
                </p>
                <Button className="w-full" onClick={goToRegistry}>
                  Vai all'Anagrafica
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {status?.step === 'add_people' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Aggiungi Proprietari e Residenti
                </CardTitle>
                <CardDescription>
                  Inserisci i dati dei proprietari e residenti delle unità
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Vai alla sezione Anagrafica per aggiungere le persone 
                  (proprietari, inquilini, usufruttuari) e associarle alle unità.
                </p>
                <Button className="w-full" onClick={goToRegistry}>
                  Vai all'Anagrafica
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}

          {status?.step === 'invite_residents' && (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="h-5 w-5" />
                  Invita i Residenti
                </CardTitle>
                <CardDescription>
                  Invia inviti ai residenti per registrarsi sulla piattaforma
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Puoi invitare i residenti via email o WhatsApp. 
                  Riceveranno un link per completare la registrazione.
                </p>
                <Button className="w-full" onClick={goToInvites}>
                  Vai agli Inviti
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        {/* Skip onboarding (optional) */}
        {status?.step && !['password_change', 'privacy_consent'].includes(status.step) && (
          <div className="text-center mt-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              Completa più tardi
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
